import React, { useState, useRef, useEffect } from 'react';
import { DropdownProps } from 'semantic-ui-react';
import { FarmersService } from '~/api/services/farmers/index';
import { FormFields } from '../FormFields';
import { useDebounce } from 'use-lodash-debounce';
import { useTranslation } from 'react-i18next';
import { FieldValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { CSSProperties } from 'styled-components';
interface Option {
  value: string;
  text: string;
}

interface Props {
  options?: Option[];
  control: any
  maxHeightMenu?: CSSProperties['maxHeight']
  values?: any[]
  useDistrictService?: boolean
  subFarmers?: boolean
  setValue: UseFormSetValue<FieldValues>
}

const InfitiveScrollDropown: React.FC<Props & DropdownProps> = (props) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation()

  const {
    subFarmers,
    setValue,
    ...infiniteScrollDropProps
  } = props

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(props.options);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState(false)
  const pageRef = useRef(null)
  const isFirstRun = useRef(true); // add a ref to track the first run of useEffect
  const debouncedValueFarmer = useDebounce(searchQuery, 300)
  const watchValue = useWatch({ control: props.control, name: props.name }) as unknown | string;
  const [selectedItem, setSelectedItem] = useState([])

  useEffect(() => {
    if (watchValue && !options.find(option => option.value === watchValue)) {
      fetchFarmerAndAddToOptions()
    }
  }, [])

  async function fetchFarmerAndAddToOptions() {
    setLoading(true);
    try {
      const response = await FarmersService.getShowFarmerData(watchValue as string)
      setSelectedItem([{ value: watchValue, text: response.data?.farmer?.companyName }])
      setOptions([{ value: watchValue as string, text: response.data?.farmer?.companyName }])
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    pageRef.current = page
  }, [page])

  useEffect(() => {
    if (open) { // only call handleSearchFarmers() after the first run
      loadMoreOptions({ page: 0, options: [], hasMore: true })
    } else {
      isFirstRun.current = false; // update ref after the first run
    }
  }, [debouncedValueFarmer]);

  useEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown && !searchQuery) {
      return;
    }

    function handleScroll() {
      const { scrollTop, scrollHeight, clientHeight } = dropdown;

      if (scrollTop + clientHeight >= scrollHeight) {
        loadMoreOptions();
      }
    }

    dropdown.addEventListener('scroll', handleScroll);

    return () => {
      dropdown.removeEventListener('scroll', handleScroll);
    };
  }, [open, hasMore, searchQuery]);

  const loadMoreOptions = async (defaultValues = null) => {
    const hasMoreWithSearch = defaultValues ? defaultValues.hasMore : hasMore
    if (loading || !hasMoreWithSearch) {
      return;
    }

    setLoading(true);

    try {
      let newOptions
      let response
      if(!props.useDistrictService) {
        response = await FarmersService.getFarmers({
          page: defaultValues ? defaultValues.page : pageRef.current,
          search: searchQuery,
          selected: props.values?.map(value => value?.id) || [],
          isMain: subFarmers ? null : 1
        })
        newOptions = response.data.map(item => ({ value: item.id, text: item.companyName }))
      } else {
        response = await FarmersService.getDistrictList({
          page: defaultValues ? defaultValues.page : pageRef.current,
          search: searchQuery,
          selected: props.values?.map(value => value?.id) || []
        })
        newOptions = response.data.map(item => ({ value: item.id, text: `${item.name} (${item.zip})` }))
      }
      if (newOptions) {
        setOptions((prevOptions) => defaultValues ? [...newOptions] : [...prevOptions, ...newOptions]);
        setHasMore(response.data.length !== 0);
        setPage(defaultValues ? defaultValues?.page + 1 : pageRef.current + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.SyntheticEvent<HTMLElement>, data: { searchQuery: string }) => {
    setSearchQuery(data.searchQuery);
  };

  const handleClose = (e) => {
    dropdownRef.current = null;
    if(!e) {
      setOpen(false)
      setSearchQuery('')
      setPage(1)
      setHasMore(true)
      if (selectedItem.length > 0) {
        setOptions(selectedItem)
      }
    }
  };

  function handleChange(e, data) {
    setOpen(false)
    if(props.onChange) {
      let farmer
      const newOptions = options.filter(option => {
        if(option.value === data.value) {
          farmer = option
          return false
        }
        return true
      })
      setOptions(newOptions)
      props.onChange(e, farmer)
    } else {
      setValue(props.name, data.value)
      setHasMore(true)
      setPage(1)
      if (data.value) {
        const option = options.find(option => option.value === data.value)
        setSelectedItem([option])
        setOptions([option])
      } else {
        setSelectedItem([])
        setOptions([])
      }
    }
    setSearchQuery('')
  }

  const handleOpen = () => {
    if (!dropdownRef.current) {
      const parentElement = document.getElementById(props.id || 'dropdown-farmers-menu-create');

      // Get all child elements of the parent element
      const children = parentElement.children;

      // Filter the child elements to select only those with the specified class
      const visibleMenuTransitions = Array.from(children).filter(child => {
        return child.className.toString() === 'menu transition';
      });
      dropdownRef.current = visibleMenuTransitions[0] as HTMLDivElement
      setOpen(true)
      setOptions([...props.options])
    }
  };
  
  return (
    <FormFields.Select
      {...infiniteScrollDropProps}
      control={props.control}
      maxHeightMenu={props.maxHeightMenu}
      onChange={handleChange}
      placeholder={props.placeholder || t('Select farmer')}
      selection
      clearable
      onClose={handleClose}
      name={props.name}
      searchQuery={searchQuery}
      noResultsMessage={t('No results found....')}
      loading={loading}
      value={watchValue as string}
      id={props.id || 'dropdown-farmers-menu-create'}
      search={(options, query) => {
        return options;
      }}
      options={options}
      ref={dropdownRef}
      onSearchChange={handleSearchChange}
      onOpen={handleOpen}
    />
  );
};

export default InfitiveScrollDropown;